import React, { useContext } from 'react'
import Div from '@jarvis.oddle/core/components/Div'
import {
  colorConstants,
  margin,
  marginSizes,
  padding,
  paddingSizes
} from '@jarvis.oddle/core/theme/default'
import IconAddressPin from '@jarvis.oddle/core/components/Icons/IconAddressPin'
import { P } from '@jarvis.oddle/core/components/Typo'
import Divider from '@jarvis.oddle/core/components/Divider'
import AddressTypeSelector from 'src/components/molecules/AddressTypeSelector'
import Form, { useFormContext } from '@jarvis.oddle/core/components/Form'
import styled from 'styled-components'
import { Column, Grid, Row } from '@jarvis.oddle/core/components/Grid'
import breakpoint from 'src/utils/styledComponentsBreakpoint'
import IconButton from '@jarvis.oddle/core/components/IconButton'
import { TranslationContext } from '@jarvis.oddle/core/components/Translation'
import useAppSelector from 'src/hooks/useAppSelector'
import { selectCheckout } from 'src/redux/checkout'

const StyledInput = styled(Column).attrs((props) => ({
  sm: 12,
  md: props.md || 12
}))`
  margin-top: ${margin[marginSizes.none]};
  ${breakpoint('tablet')`
    width: ${(props) => (props.width ? props.width : '100%')};
    ${(props) =>
      props.width === '30%'
        ? `padding-right: ${padding[paddingSizes.small]}`
        : ''}
    ${(props) =>
      props.width === '70%'
        ? `padding-left: ${padding[paddingSizes.small]}`
        : ''}
  `}
`

const StyledIconButton = styled(IconButton)`
  opacity: 1;
  border: 1px solid ${colorConstants.black};
  border-radius: 5px;
  justify-content: end;
  margin-bottom: ${margin[marginSizes.medium]};
  ${breakpoint('tablet')`
    height: 60px;
  `}
`

const StyledButtonText = styled(P).attrs(() => ({
  textAlign: 'left'
}))`
  color: ${colorConstants.black};
`

const FormTextArea = styled(Form.TextArea)`
  resize: none;
  border: none;
`

interface DeliveryAddressProps {
  stepAddressFieldsRequired: Record<string, string>
  addressTwoFormat: Oms.V4.AddressTwoFormat[]
}

const DeliveryAddress = ({
  stepAddressFieldsRequired,
  addressTwoFormat
}: DeliveryAddressProps): JSX.Element => {
  const { t } = useContext(TranslationContext)
  const { errors, values, setFieldValue } = useFormContext()
  const checkoutState = useAppSelector(selectCheckout)
  const { deliveryAddressTwoDetails, deliveryAddressTwo } =
    checkoutState.meta.data.storeOrder || {}
  const { addressType } = values
  const renderAddressInput =
    addressType && stepAddressFieldsRequired[addressType]

  const setFormattedContent = React.useCallback(
    (text) => {
      if ((text || '').length > 140) return
      setFieldValue('deliveryInstructions', text)
    },
    [setFieldValue]
  )

  const getFieldName = (field, isRequired) => {
    switch (field) {
      case 'unitNumber':
        return `${t('Unit Number')} ${
          !isRequired ? `(${t('Optional')})` : ''
        }`.trim()
      case 'buildingName':
        return `${t('Building Name')} ${
          !isRequired ? `(${t('Optional')})` : ''
        }`.trim()
      case 'houseNumber':
        return `${t('House Number')} ${
          !isRequired ? `(${t('Optional')})` : ''
        }`.trim()
      default:
        return ''
    }
  }

  const handleSelectAddressType = (addressType) => {
    setFieldValue('addressType', addressType)
    Object.keys(stepAddressFieldsRequired[addressType] || {}).map(
      (field, index) => {
        if ((deliveryAddressTwoDetails || {})[field] && (deliveryAddressTwoDetails || {})[field] !== '-') {
          setFieldValue(
            field,
            (deliveryAddressTwoDetails || {})[field]
          )
        } else if (index === 0 && deliveryAddressTwo && !deliveryAddressTwo.includes('-,')) {
          setFieldValue(
            field,
            deliveryAddressTwo
          )
        } else {
          setFieldValue(
            field,
            ''
          )
        }
      }
    )
  }

  return (
    <React.Fragment>
      <Div marginBottom={marginSizes.small}>
        <StyledIconButton
          fullWidth
          Icon={IconAddressPin}
          disabled
          buttonColor={colorConstants.lightestGray}
          ButtonText={StyledButtonText}
        >
          {values.deliveryAddressAutoComplete}
        </StyledIconButton>
      </Div>
      <Divider />
      {Object.keys(stepAddressFieldsRequired || {}).length > 1 && (
        <AddressTypeSelector
          addressTwoFormat={addressTwoFormat}
          selectedAddressType={addressType}
          onSelectAddressType={handleSelectAddressType}
        />
      )}
      {errors.selector && (
        <Form.FieldError>{t(errors.selector)}</Form.FieldError>
      )}
      <Grid>
        <Row>
          {addressType &&
            Object.keys(renderAddressInput || {}).map((value, index) => {
              return (
                <StyledInput
                  md={
                    Object.keys(renderAddressInput).length > 1
                      ? index % 2 === 0
                        ? 4
                        : 8
                      : 12
                  }
                  width={
                    Object.keys(renderAddressInput).length > 1
                      ? index % 2 === 0
                        ? '30%'
                        : '70%'
                      : '100%'
                  }
                  paddingRight={index % 2 === 0 && padding[paddingSizes.none]}
                >
                  <Form.Input
                    type='text'
                    name={value}
                    label={getFieldName(
                      value,
                      stepAddressFieldsRequired[addressType][value].required
                    )}
                    fieldTouched
                  />
                </StyledInput>
              )
            })}
        </Row>
      </Grid>
      <FormTextArea
        name='deliveryInstructions'
        label={t(
          'Delivery Instructions e.g. Building opposite Starbucks (Optional)'
        )}
        onChange={(event) => setFormattedContent(event.value)}
      />
    </React.Fragment>
  )
}

export default DeliveryAddress
